import { LocaleDto } from '../../../../../../api/src/creative-unit-package/models/locale.dto';
import { Clip } from '../clip';
import { Music } from '../music';
import { StorySegment } from '../story-segment';
import { Voiceover } from '../voiceover/voiceover.model';
import { StoryFormat } from '../story-format';
import { StrapiLocale } from './strapi-locale.model';

export type ApprovalMode = 'disabled' | 'simple' | 'advanced';

export interface PageMeta {
	id: string;
	name: string;
	valid?: boolean;
	visible?: boolean;
	nextLabel?: string;
	hideNext?: boolean;
	disabledReason?: string;
	icon?: string;
}

export interface GlobalSettings {
	id: string;
	name: string;
	organizationLogo: StrapiSingleResponse<StrapiMedia>;
	signupPageImage: StrapiSingleResponse<StrapiMedia>;
	loginPageImage: StrapiSingleResponse<StrapiMedia>;
	organizationName: string;
	organizationSlug: string;
	siteTitle: string;
	siteFaviconUrl: string;
	assetProvider: string;
	disclaimer: string;
	defaultClipPath: string;
	defaultLocale: string;
	defaultMusicPath: string;
	defaultVoiceoverPath: string;
	getStartedStoryFormatTitle: string;
	getStartedStoryFormatInstruction: string;
	getStartedVideoFormatTitle: string;
	getStartedVideoFormatInstruction: string;
	getStartedStoryTitle: string;
	getStartedStoryInstruction: string;
	heroGetStartedTitle: string;
	heroGetStartedSubtitle: string;
	heroGetStartedDescription: string;
	heroGetStartedVideo: string;
	heroGetStartedBackground: StrapiSingleResponse<StrapiMedia>;
	finalizeVideoPreviewTitle: string;
	finalizeVideoPreviewInstruction: string;
	customStyles: any;
	terms: string;
	signupCopy: string;
	creativePackageCategories: Category[];
	transcriptDownloadEnabled: boolean;
	approvalMode?: ApprovalMode;
	previewShareLinksEnabled: boolean;
	exportShareLinksEnabled: boolean;
	projectStateExportEnabled: boolean;
	disableCheckout: boolean;
	downloadReceiptEnabled: boolean;
	transcriptDownloadInstructions: string;
	transcriptDownloadHeader: string;
	transcriptDownloadCta: string;
	reviewPreviewHeader: string;
	reviewPreviewInstructions: string;
	previewShareLinksInstructions: string;
	offlinePaymentImportTitle: string;
	offlinePaymentImportInstruction: string;
	offlinePaymentImportDialogTitle: string;
	offlinePaymentImportDialogInstruction: string;
	offlinePaymentFileName: string;
	offlinePaymentOnboardDialogTitle: string;
	offlinePaymentOnboardDialogCopy: string;
	freeTrialEnabled: boolean;
	freeContentTaggingEnabled: boolean;
	freeTrialPurchaseDialogCopy: DialogConfig;
	freeTrialRegions: any;
	freeTrialStartTitle: string;
	freeTrialStartCopy: string;
	premiumContentCopy: string;
	paymentMethodsRequired: boolean;
	disableTermsPopup: boolean;
	billingVerificationThreshold: number;
	enableIntercom: any;
	disableVersion1Projects: boolean;
	enableHotjar: boolean;
	businessNameLabelPlural: string;
	businessNameLabelSingular: string;
	csvDownloadExportEnabled: boolean;
	metadata: any;
	helpLink?: string;
	showInReviewSection: boolean;
	showInProgressSection: boolean;
	bodyScripts: string;
	locales: StrapiLocale[];
	signupExtraPrivacyConditions: boolean;
	signupCrossBorderTransfer: string;
	signupPrivacyPolicy: string;
	footerCopyrightText: string;
	showFooter: boolean;
	footerLinks: ActionLink[];
	headerNavLinks?: HeaderNavLink[];
	footerLayout: string;
	footerCopy: string;
	projectApprovalRequired?: boolean;
	exportsRequireApproval: boolean;
	projectPageCheckoutButtonLabel: string;
	projectPageCheckoutDisabledTooltip: string;
	projectPageCheckoutTitle: string;
	checkoutSubtitle: string;
	checkoutButtonLabel: string;
	downloadPageCongratulationsTitle: string;
	checkoutTermsSubtitle: string;
	disableReceiptButton: boolean;
	enableNewCreativePackageRequests: boolean;
	disableProjectHoverOverlay: boolean;
	creativePackageRequestFormId: string;
	hideSignUpButton: boolean;
	hideBusinessAddButton: boolean;
	enableCustomExportFileNames: boolean;
	exportProgressMessages: TextItem[];
	enableCreativePackageTerms: boolean;
	creativePackageTerms: string;
	enableExportActions: boolean;
	disablePsdExport: boolean;
	disableAiExport: boolean;
	disableAnimatedGifExport: boolean;
	showDuplicateTemplateButton: boolean;
	enableContactSupportNavItem: boolean;
	contactSupportNavItemTitle: string;
	contactSupportNavItemBody: string;
	emailAuthEnabled: boolean;
	phoneAuthEnabled?: boolean;
	unitPackageListingSizeLabelSingular: string;
	unitPackageListingSizeLabelPlural: string;
	disableUnitSizeDialogVariations: boolean;
	invalidEmailDomainMessage: string;
	createBusinessNameGuidelines?: string; // Optional guidelines for creating a business name
	newUsersJoinBusiness: boolean;
	overrideEmailWhitelist: boolean;
	disableBusinessTags: boolean;
	canChangeExportFilenames: boolean;
	variationsEnabled: boolean;
	enableProjectComments: boolean;
	canChangeTheme: boolean;
	aiResizeEnabled?: boolean;
	enableSaveToProjectFiles: boolean;

	/**
	 * Per-site opt-in (default off) that grants scoped reporting access to
	 * business admins (Owner/Administrator on ≥1 business). The full-access
	 * tier (SuperAdmin, site Admin, reporting flag) is unaffected by this
	 * toggle. Mirrors the API `Settings.enableBusinessAdminReporting` field;
	 * reaches the web app through the existing global settings store.
	 */
	enableBusinessAdminReporting?: boolean;

	// File Library Settings

	/**
	 * Site-level file library configuration, owned by Settings -> File Library.
	 * `fileLibrarySettings.enableFileLibrary` is the runtime toggle that decides whether the
	 * library is exposed to users; it never affects `enableFileLibrarySettings` below.
	 */
	fileLibrarySettings?: FileLibrarySettings;

	/**
	 * CMS-owned master switch (organization-setting `enableFileLibrarySettings`): does this org
	 * have the file library feature at all. Toggled only in the CMS — read-only to the app, and
	 * never overwritten from an API site-settings response (see `CMS_OWNED_SETTING_KEYS`).
	 * Gates admin access to Settings -> File Library, and every user-facing file library
	 * surface additionally requires `fileLibrarySettings.enableFileLibrary`.
	 */
	enableFileLibrarySettings?: boolean;
}

/**
 * Type for starting page options, chosen in Settings -> File Library.
 * - 'legacy-dashboard' - Traditional projects dashboard
 * - 'file-library-projects' - File library with projects tab active
 * - 'file-library-home' - File library with home/files view
 */
export type StartingPageOption = 'legacy-dashboard' | 'file-library-projects' | 'file-library-home';

/**
 * Locale data for file library settings.
 * Keys are field names, values are locale objects mapping locale codes to translated strings.
 */
export interface FileLibraryLocale {
	/** Locale translations for the projects menu label. Key is locale code (e.g., 'en', 'es'), value is translated string. */
	projectsMenuLabel?: { [localeCode: string]: string };
}

/**
 * File Library configuration settings for a site (stored in database)
 */
export type SidebarNavigationItemType = 'builtin' | 'content-page' | 'agents-section' | 'section-header' | 'separator';

export interface SidebarNavigationItem {
	/**
	 * Unique key: 'home' | 'starred' | 'projects' | 'collections' | 'agents-section' |
	 * 'content-page:<pageId>' | 'section-header:<uuid>' | 'separator:<uuid>'
	 */
	key: string;
	type: SidebarNavigationItemType;
	label?: string;
	/** Locale translations for the label. Key is locale code (e.g. 'en'), value is the translated label. */
	labelLocale?: LocaleDto;
	visible: boolean;
	/** For content-page type: the linked content page ID */
	contentPageId?: string;
	/** For content-page type: the linked content page slug */
	contentPageSlug?: string;
	/** Material icon name override for the sidebar entry. For content-page type, snapshot of ContentPage.icon at link time. */
	icon?: string;
	/**
	 * Key of the `section-header` item this entry belongs to. Membership only:
	 * array order still drives display order, `parentKey` only decides grouping
	 * and indentation. Absent means the item renders at root level even when it
	 * follows a section header. Only `section-header` items may be parents;
	 * section headers and separators never carry one.
	 */
	parentKey?: string;
}

export interface FileLibrarySettings {
	enableFileLibrary?: boolean;
	/** Allow users to create their own collections */
	allowUserCollections: boolean;
	/**
	 * Optional starting folder ID for the file library.
	 * When set, this becomes the "virtual root" for the site's file library view.
	 * Can be overridden at the business level.
	 */
	startingFolderId?: string | null;

	/**
	 * Enable the team custom starting folder.
	 * When set, this allows teams to set their own custom starting folder for the file library.
	 */
	enableBusinessCustomStartingFolder?: boolean;

	/**
	 * Start the file library sidebar in its collapsed icon-only rail.
	 *
	 * A default for users who have never collapsed or expanded the sidebar
	 * themselves, not an override: once a user touches it, their own choice is
	 * persisted per browser and wins from then on.
	 */
	defaultSidebarCollapsed?: boolean;
	/**
	 * Where users land after login, chosen in Settings -> File Library.
	 * Unset, or the library switched off, means `legacy-dashboard`.
	 * Read it through `resolveStartingPage()`.
	 */
	startingPage?: StartingPageOption;

	/** Custom label for the "Projects" menu item. Defaults to "Projects" if not set. */
	projectsMenuLabel?: string;
	/** Locale translations for file library settings */
	locale?: FileLibraryLocale;
	/** Ordered flat navigation registry for the file library sidebar */
	sidebarNavigation?: SidebarNavigationItem[];
}

export function getGlobalQuery(siteId: string) {
	return `
		{
			globalSet(siteId: "${siteId}") {
				id
				... on siteSettings_GlobalSet {
					id
					name
					organizationName
					organizationSlug
					organizationLogo {
					url
					}
					loginPageImage {
					url
					}
					signupPageImage {
					url
					}
					getStartedVideoFormatTitle
					getStartedVideoFormatInstruction
					getStartedStoryTitle
					getStartedStoryInstruction
					finalizeVideoPreviewTitle
					finalizeVideoPreviewInstruction
				}
				siteId
			}
		}
	`;
}

export interface GlobalQueryResponse {
	globalSet: GlobalSettings;
}

export interface ValidationError {
	type: string; // Slug-like id for error types
	message: string;
}

export interface EncodeJobObject {
	storyFormat: StoryFormat;
	segments: StorySegment[];
	clips: Clip[]; // TODO
	music: Music;
	voiceovers?: Voiceover[];
}

export interface StrapiSingleResponse<T> {
	data: StrapiEntity<T>;
	meta: StrapiMeta;
}

export interface StrapiCollectionResponse<T> {
	data: StrapiEntity<T>[];
	meta: StrapiMeta;
}

export interface StrapiEntity<T> {
	id: string;
	attributes: T;
}

export interface StrapiMedia {
	alternativeText: string;
	caption: string;
	createdAt: string;
	ext: string;
	formats: any;
	hash: string;
	height: number;
	mime: string;
	name: string;
	previewUrl: string;
	provider: string;
	provider_metadata: any;
	size: number;
	updatedAt: string;
	url: string;
	width: number;
}

export interface StrapiMeta {}

export interface Asset {
	id: string; // Combo of story id, segment id and cms id.
	cmsId?: string; // Id from the External CMS
	storyId?: string;
	segmentId?: string;
	name: string;
	filename: string;
	format: string;
	type: string;
	size: number;
	duration: number;
	assetFilters?: AssetFilter[];
	createdAt: string;
	customFields: { [key: string]: any };
	urls: {
		preview: string;
		original: string;
	};
	isCustomVideo?: boolean;
}

export interface AssetFilter {
	id: number;
	label: string;
	value: string;
	filterCriteria: 'include' | 'exclude';
	entityType: string;
	tags: string;
	overrideParentEntityFilters: boolean;
	freeContent: boolean;
}

export interface AssetQueryRequest {
	provider: string;
	path: string;
	includeTags?: string; // JSON stringify array of tags to include
	excludeTags?: string; // JSON stringify array of tags to exclude
}

export interface ValueChange {
	key: string;
	value: any;
}

export interface VideoPlayerControl {
	action: 'play' | 'pause' | 'stop' | 'seek' | 'restart';
	seek?: number;
}

export interface Category {
	id: string;
	slug: string;
	name: string;
}

export interface Country {
	alpha2Code: string;
	alpha3Code: string;
	createdAt: string;
	name: string;
	updatedAt: string;
}

export interface DialogButton {
	caption: string;
	size: string;
	color: string;
	type: string;
	action?: string;
	actionParameter?: string;
}

export interface ActionLink {
	label: string;
	icon: string;
	url?: string;
	linkTarget: '_self' | '_blank' | '_parent' | '_top' | string;
	cssClass: string;
	action?: 'openLink' | 'showTerms' | string;
}

export interface HeaderNavLink {
	label: string;
	url: string;
	target?: '_self' | '_blank' | '_parent' | '_top' | string;
}

export interface TextItem {
	title: string;
	duration: number;
}

export interface DialogConfig {
	title?: string;
	message?: string;
	confirm?: string;
	cancel?: string;
}

/**
 * This object helps wrap debounced changes so that you can pipe through
 * debounce methods before subscribing and doing something with the value.
 */
export interface DebouncedValueChange extends ValueChange {
	debounceTime: number;
}

export function flattenStrapiCollection<T>(collection: StrapiCollectionResponse<T>): T[] {
	return collection?.data?.map(e => flattenStrapiEntity(e)) || [];
}

export function flattenStrapiSingleResponse<T>(item: StrapiSingleResponse<T>): T {
	return item?.data?.id
		? {
				id: item?.data?.id,
				...item?.data?.attributes
			}
		: undefined;
}

export function flattenStrapiEntity<T>(entity: StrapiEntity<T>): T {
	return {
		id: entity?.id,
		...entity?.attributes
	};
}
